import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { opsFetch } from "../lib/api";

type CatalogRes = {
  version: string;
  catalog: {
    cityHubs?: Record<string, { hotels?: string }>;
    generalServices?: Record<string, { name?: string; affiliateLink?: string }>;
    klookBySlug?: Record<string, { affiliateUrl?: string; city?: string }>;
    hotelsByCity?: Record<string, Array<{ name: string; tier: string; bookingUrl: string }>>;
  };
};

type Tab = "hubs" | "services" | "klook" | "hotels" | "cta";

export function AffiliatePage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("hubs");
  const [cityFilter, setCityFilter] = useState("beijing");
  const [msg, setMsg] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["ops-affiliate"],
    queryFn: () => opsFetch<CatalogRes>("/api/ops/affiliate/catalog"),
  });

  const cta = useQuery({
    queryKey: ["ops-cta-catalog"],
    queryFn: () =>
      opsFetch<{
        data: {
          cities: Array<{ slug: string; hotelsUrl?: string; hotelCount: number }>;
          services: Array<{ key: string; name?: string; affiliateLink?: string }>;
          experiences: Array<{
            slug: string;
            affiliateUrl?: string;
            city?: string;
            nameEn?: string;
          }>;
        };
      }>("/api/ops/affiliate/cta-catalog"),
    enabled: tab === "cta",
  });

  const patchHub = useMutation({
    mutationFn: ({ slug, hotels }: { slug: string; hotels: string }) =>
      opsFetch(`/api/ops/affiliate/city-hubs/${encodeURIComponent(slug)}`, {
        method: "PATCH",
        body: JSON.stringify({ hotels }),
      }),
    onSuccess: () => {
      setMsg("Hub updated");
      void qc.invalidateQueries({ queryKey: ["ops-affiliate"] });
    },
  });

  const patchService = useMutation({
    mutationFn: ({ key, affiliateLink }: { key: string; affiliateLink: string }) =>
      opsFetch(`/api/ops/affiliate/services/${encodeURIComponent(key)}`, {
        method: "PATCH",
        body: JSON.stringify({ affiliateLink }),
      }),
    onSuccess: () => {
      setMsg("Service updated");
      void qc.invalidateQueries({ queryKey: ["ops-affiliate"] });
    },
  });

  const putExp = useMutation({
    mutationFn: ({ slug, affiliateUrl }: { slug: string; affiliateUrl: string }) =>
      opsFetch(`/api/ops/affiliate/experiences/${encodeURIComponent(slug)}`, {
        method: "PUT",
        body: JSON.stringify({ affiliateUrl }),
      }),
    onSuccess: () => {
      setMsg("Experience link updated");
      void qc.invalidateQueries({ queryKey: ["ops-affiliate"] });
    },
  });

  const hubs = useMemo(
    () => Object.entries(q.data?.catalog.cityHubs || {}).filter(([k]) => k !== "_default"),
    [q.data],
  );
  const services = useMemo(
    () => Object.entries(q.data?.catalog.generalServices || {}),
    [q.data],
  );
  const klook = useMemo(() => Object.entries(q.data?.catalog.klookBySlug || {}), [q.data]);
  const cities = useMemo(
    () => Object.keys(q.data?.catalog.hotelsByCity || {}).sort(),
    [q.data],
  );
  const hotels = q.data?.catalog.hotelsByCity?.[cityFilter] || [];
  const emptyHubs = hubs.filter(([, hub]) => !(hub.hotels || "").trim()).length;
  const emptyKlook = klook.filter(([, row]) => !row.affiliateUrl).length;

  return (
    <>
      {q.data ? (
        <p className={emptyHubs + emptyKlook > 0 ? "ops-callout" : "ops-callout ops-callout--ok"}>
          {emptyHubs + emptyKlook > 0
            ? `${emptyHubs} city hubs and ${emptyKlook} Klook slugs have no booking link. An empty link cannot send a click.`
            : "Booking links are filled. Use this page to replace a broken URL, not to browse the catalog."}
        </p>
      ) : null}
      <div className="ops-tabs">
        {(
          [
            ["hubs", "City hubs"],
            ["services", "Services"],
            ["klook", "Klook by slug"],
            ["hotels", "Hotels (read)"],
            ["cta", "CTA catalog (read)"],
          ] as const
        ).map(([id, label]) => (
          <button
            className={tab === id ? "ops-tab ops-tab--active" : "ops-tab"}
            key={id}
            onClick={() => setTab(id)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>
      {msg ? <p className="ops-ok">{msg}</p> : null}
      {q.isLoading ? <p className="ops-muted">Loading catalog…</p> : null}
      {q.isError ? <p className="ops-error">{(q.error as Error).message}</p> : null}
      {q.data ? (
        <p className="ops-muted">
          Version: <code>{q.data.version}</code>
        </p>
      ) : null}

      {tab === "hubs" && q.data ? (
        <div className="ops-panel">
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>City</th>
                  <th>Hotels hub URL</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {hubs.map(([slug, row]) => (
                  <HubRow
                    busy={patchHub.isPending}
                    hotels={row.hotels || ""}
                    key={slug}
                    onSave={(hotels) => patchHub.mutate({ slug, hotels })}
                    slug={slug}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "services" && q.data ? (
        <div className="ops-panel">
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Name</th>
                  <th>Affiliate link</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {services.map(([key, row]) => (
                  <ServiceRow
                    affiliateLink={row.affiliateLink || ""}
                    busy={patchService.isPending}
                    key={key}
                    name={row.name || key}
                    onSave={(affiliateLink) => patchService.mutate({ key, affiliateLink })}
                    serviceKey={key}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "klook" && q.data ? (
        <div className="ops-panel">
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Slug</th>
                  <th>City</th>
                  <th>Affiliate URL</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {klook.slice(0, 80).map(([slug, row]) => (
                  <KlookRow
                    affiliateUrl={row.affiliateUrl || ""}
                    busy={putExp.isPending}
                    city={row.city || ""}
                    key={slug}
                    onSave={(affiliateUrl) => putExp.mutate({ slug, affiliateUrl })}
                    slug={slug}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "hotels" && q.data ? (
        <div className="ops-panel">
          <div className="ops-toolbar">
            <select
              className="ops-input"
              onChange={(e) => setCityFilter(e.target.value)}
              value={cityFilter}
            >
              {cities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <span className="ops-muted">Deep links are edited via catalog sync / future hotel editor.</span>
          </div>
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tier</th>
                  <th>Booking URL</th>
                </tr>
              </thead>
              <tbody>
                {hotels.map((h) => (
                  <tr key={`${h.name}-${h.bookingUrl}`}>
                    <td>{h.name}</td>
                    <td>{h.tier}</td>
                    <td>
                      <a href={h.bookingUrl} rel="noreferrer" target="_blank">
                        open
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "cta" ? (
        <div className="ops-panel">
          {cta.isLoading ? <p className="ops-muted">Loading CTA catalog…</p> : null}
          {cta.isError ? <p className="ops-error">{(cta.error as Error).message}</p> : null}
          {cta.data ? (
            <>
              <h3>City hotel CTAs</h3>
              <div className="ops-table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <th>City</th>
                      <th>Hotels in catalog</th>
                      <th>Hub URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cta.data.data.cities.map((c) => (
                      <tr key={c.slug}>
                        <td>
                          <code>{c.slug}</code>
                        </td>
                        <td>{c.hotelCount}</td>
                        <td className="ops-muted">{c.hotelsUrl || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <h3>Experience / Klook CTAs</h3>
              <div className="ops-table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <th>Slug</th>
                      <th>City</th>
                      <th>Name</th>
                      <th>URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cta.data.data.experiences.map((e) => (
                      <tr key={e.slug}>
                        <td>
                          <code>{e.slug}</code>
                        </td>
                        <td>{e.city || "—"}</td>
                        <td>{e.nameEn || "—"}</td>
                        <td className="ops-muted">{e.affiliateUrl || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function HubRow({
  slug,
  hotels,
  onSave,
  busy,
}: {
  slug: string;
  hotels: string;
  onSave: (hotels: string) => void;
  busy: boolean;
}) {
  const [value, setValue] = useState(hotels);
  return (
    <tr>
      <td>
        <code>{slug}</code>
      </td>
      <td>
        <div className="ops-url-edit">
          <input className="ops-input" onChange={(e) => setValue(e.target.value)} value={value} />
        </div>
      </td>
      <td>
        <button
          className="ops-btn ops-btn--primary"
          disabled={busy || value === hotels}
          onClick={() => onSave(value)}
          type="button"
        >
          Save
        </button>
      </td>
    </tr>
  );
}

function ServiceRow({
  serviceKey,
  name,
  affiliateLink,
  onSave,
  busy,
}: {
  serviceKey: string;
  name: string;
  affiliateLink: string;
  onSave: (link: string) => void;
  busy: boolean;
}) {
  const [value, setValue] = useState(affiliateLink);
  return (
    <tr>
      <td>
        <code>{serviceKey}</code>
      </td>
      <td>{name}</td>
      <td>
        <input className="ops-input" onChange={(e) => setValue(e.target.value)} value={value} />
      </td>
      <td>
        <button
          className="ops-btn ops-btn--primary"
          disabled={busy || value === affiliateLink}
          onClick={() => onSave(value)}
          type="button"
        >
          Save
        </button>
      </td>
    </tr>
  );
}

function KlookRow({
  slug,
  city,
  affiliateUrl,
  onSave,
  busy,
}: {
  slug: string;
  city: string;
  affiliateUrl: string;
  onSave: (url: string) => void;
  busy: boolean;
}) {
  const [value, setValue] = useState(affiliateUrl);
  return (
    <tr>
      <td>
        <code>{slug}</code>
      </td>
      <td>{city || "—"}</td>
      <td>
        <input className="ops-input" onChange={(e) => setValue(e.target.value)} value={value} />
      </td>
      <td>
        <button
          className="ops-btn ops-btn--primary"
          disabled={busy || value === affiliateUrl}
          onClick={() => onSave(value)}
          type="button"
        >
          Save
        </button>
      </td>
    </tr>
  );
}
